import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { DialogContent, DialogFooter } from '../ui/dialog';

import { WorkTripFields, DeductionsSection } from './form';
import UpliftSection from './form/UpliftSection';
import ContractDialog from '../contracts/ContractDialog';
import TripWizardStep1 from './TripWizardStep1';
import TripWizardStep2 from './TripWizardStep2';
import TripAlertDialogs from './TripAlertDialogs';
import { TripWizardHeader, TripWizardFooter } from './TripWizardChrome';
import { deriveQAFields, getInitialFormData } from './tripDialogUtils';
import { buildTripPayload, applyDateFallbacks, checkForOverlaps, canGoNextStep } from './tripPayloadBuilder';
import { useListState } from './useListState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DATE_FIELDS = [
  'date_left_uk', 'date_returned_uk', 'date_joined_vessel', 'date_left_vessel',
  'pre_departure_date', 'post_return_date',
  'pre_hotel_checkin', 'pre_hotel_checkout', 'post_hotel_checkin', 'post_hotel_checkout',
  'start_date', 'end_date'
];

const TripDialog = ({ trip, onSuccess, onClose, existingTrips = [] }) => {
  const [formData, _setFormDataRaw] = useState(getInitialFormData());
  const [dayRates, setDayRates, dayRateOps] = useListState();
  const [deductions, setDeductions, deductionOps] = useListState();
  const [uplifts, setUplifts, upliftOps] = useListState();
  const [ukCrossings, setUkCrossings, ukCrossingOps] = useListState();
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [overlapAlert, setOverlapAlert] = useState({ open: false, overlappingTrips: [], pendingPayload: null });
  const [ukDatesWarning, setUkDatesWarning] = useState({ open: false, missingFields: [], pendingPayload: null });
  const [currentStep, setCurrentStep] = useState(1);

  // Date context: tracks the last entered date so subsequent empty date fields
  // open the calendar to the same month/year (saves scrolling for historical trips)
  const [dateContext, setDateContext] = useState('');

  // Wrapped setter that also updates dateContext when a date field changes
  const setFormData = (updaterOrObj) => {
    _setFormDataRaw(prev => {
      const next = typeof updaterOrObj === 'function' ? updaterOrObj(prev) : updaterOrObj;
      for (const field of DATE_FIELDS) {
        if (next[field] && next[field] !== prev[field]) {
          setDateContext(next[field]);
          break;
        }
      }
      return next;
    });
  };

  const getTotalSteps = () => {
    const type = formData.trip_type;
    if (type === 'wage_adjustment') return 1;
    if (type === 'qualifying_holiday') return 1;
    if (type === 'work_trip') return 3; // Contract → Trip Details → Dates
    return 2; // Other contract-linkable types: Contract → Details & Dates
  };
  const totalSteps = getTotalSteps();

  // Initialize form with trip data when editing
  useEffect(() => {
    if (trip) {
      setFormData({
        trip_type: trip.trip_type || 'work_trip',
        pay_type: trip.pay_type || 'day_rate',
        uk_waters_status: trip.uk_waters_status || 'outside',
        date_left_uk: trip.date_left_uk || '',
        date_returned_uk: trip.date_returned_uk || '',
        no_uk_departure: trip.no_uk_departure || false,
        no_uk_return: trip.no_uk_return || false,
        date_joined_vessel: trip.date_joined_vessel || '',
        date_left_vessel: trip.date_left_vessel || '',
        vessel_name: trip.vessel_name || '',
        contractor: trip.contractor || '',
        port_joining: trip.port_joining || '',
        port_leaving: trip.port_leaving || '',
        foreign_ports: trip.foreign_ports?.join(', ') || '',
        hotel_name: trip.hotel_name || '',
        hotel_city: trip.hotel_city || '',
        hotel_reason: trip.hotel_reason || '',
        course_name: trip.course_name || '',
        course_provider: trip.course_provider || '',
        course_location: trip.course_location || '',
        has_multiple_uk_crossings: trip.has_multiple_uk_crossings || false,
        notes: trip.notes || '',
        leave_type: trip.leave_type || 'annual_leave',
        adjustment_type: trip.adjustment_type || 'addition',
        adjustment_description: trip.adjustment_description || '',
        adjustment_amount: trip.adjustment_amount || '',
        currency: trip.currency || 'GBP',
        actual_gbp_received: trip.actual_gbp_received || '',
        monthly_salary: trip.monthly_salary || '',
        sea_uplift_rate: trip.sea_uplift_rate || '',
        base_name: trip.base_name || '',
        base_location: trip.base_location || '',
        pre_travel: trip.pre_travel || false,
        pre_departure_date: trip.pre_departure_date || '',
        has_pre_uk: trip.has_pre_uk || false,
        pre_uk_stops: trip.pre_uk_stops || [],
        has_pre_abroad: trip.has_pre_abroad || false,
        pre_abroad_stops: trip.pre_abroad_stops || [],
        has_post_abroad: trip.has_post_abroad || false,
        post_abroad_stops: trip.post_abroad_stops || [],
        post_travel: trip.post_travel || false,
        post_return_date: trip.post_return_date || '',
        has_post_uk: trip.has_post_uk || false,
        post_uk_stops: trip.post_uk_stops || [],
        ...deriveQAFields(trip),
        has_separate_travel_rate: trip.has_separate_travel_rate || false,
        travel_day_rate: trip.travel_day_rate || '',
        travel_uplift_rate: trip.travel_uplift_rate || '',
        has_separate_hotel_rate: trip.has_separate_hotel_rate || false,
        hotel_day_rate: trip.hotel_day_rate || '',
        hotel_uplift_rate: trip.hotel_uplift_rate || '',
        contract_id: trip.contract_id || ''
      });

      // Initialize day rates — migrate from base_day_rate + uplifts if needed
      let initialDayRates = trip.day_rates || [];
      if (initialDayRates.length === 0 && trip.base_day_rate) {
        const startDate = trip.date_left_uk || trip.date_joined_vessel || '';
        const endDate = trip.date_returned_uk || trip.date_left_vessel || '';
        let effectiveRate = parseFloat(trip.base_day_rate) || 0;
        if (trip.uplifts && trip.uplifts.length > 0) {
          const allTripUplifts = trip.uplifts.filter(u => !u.date_from && !u.date_to);
          effectiveRate += allTripUplifts.reduce((sum, u) => sum + (parseFloat(u.amount) || 0), 0);
        }
        initialDayRates = [{
          date_from: startDate,
          date_to: endDate,
          rate_type: 'standard',
          daily_rate: effectiveRate,
          description: ''
        }];
      }

      const ensureKeys = (items) => items.map(item => item._key ? item : { ...item, _key: crypto.randomUUID() });
      setDayRates(ensureKeys(initialDayRates));
      setDeductions(ensureKeys(trip.deductions || []));
      setUplifts(ensureKeys((trip.uplifts || []).map(u => ({
        ...u,
        type: u.type || 'other',
        applies_to: (u.date_from && u.date_to) ? 'specific_dates' : 'whole_trip'
      }))));
      setUkCrossings(ensureKeys(trip.uk_water_crossings || []));
      setCurrentStep(1);
    } else {
      setFormData(getInitialFormData());
      setDayRates([]);
      setDeductions([]);
      setUplifts([]);
      setUkCrossings([]);
      setCurrentStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip]);

  useEffect(() => {
    axios.get(`${API_URL}/api/contracts`)
      .then(res => setContracts(res.data.contracts || []))
      .catch(() => {});
  }, []);

  // Called when a contract is created from within the Trip Wizard
  const handleContractCreated = () => {
    axios.get(`${API_URL}/api/contracts`).then(res => {
      const updated = res.data.contracts || [];
      setContracts(updated);
      if (updated.length > 0) {
        applyContract(updated[updated.length - 1]);
      }
    }).catch(() => {});
  };

  // List handlers (templates only — add/update/remove come from useListState)
  const addDayRate = () => dayRateOps.add({
    date_from: formData.date_left_uk || formData.date_joined_vessel || '',
    date_to: formData.date_returned_uk || formData.date_left_vessel || '',
    rate_type: 'standard',
    daily_rate: '',
    description: ''
  });
  const addDeduction = () => deductionOps.add({
    name: 'Company Margin', amount: 0, applies_to_month: '', description: ''
  });
  const addUplift = () => upliftOps.add({
    type: 'other', name: '', amount: '', applies_to: 'whole_trip', date_from: '', date_to: '', description: ''
  });
  const addUkCrossing = () => ukCrossingOps.add({
    date_left_uk_waters: '', date_returned_uk_waters: '', port_left: '', port_returned: '', notes: ''
  });
  const calculateTotalDeductions = () => deductions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const saveTrip = async (payload) => {
    try {
      if (trip) {
        await axios.put(`${API_URL}/api/trips/${trip.id}`, payload);
        toast.success('Trip updated successfully');
        onSuccess(trip.id);
      } else {
        const res = await axios.post(`${API_URL}/api/trips`, payload);
        toast.success('Trip created successfully');
        onSuccess(res.data?.trip?.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save trip');
    } finally {
      setLoading(false);
    }
  };

  const runOverlapCheck = () => {
    const tripStart = formData.date_left_uk || formData.date_joined_vessel;
    const tripEnd = formData.date_returned_uk || formData.date_left_vessel;
    return checkForOverlaps(existingTrips, trip, tripStart, tripEnd, formData.no_uk_departure, formData.no_uk_return);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = buildTripPayload(formData, { contracts, dayRates, uplifts, deductions, ukCrossings });
    applyDateFallbacks(payload, formData);

    const overlappingTrips = runOverlapCheck();
    if (overlappingTrips.length > 0) {
      setOverlapAlert({ open: true, overlappingTrips, pendingPayload: payload });
      setLoading(false);
      return;
    }

    await saveTrip(payload);
  };

  const handleOverlapConfirm = async () => {
    setLoading(true);
    setOverlapAlert({ open: false, overlappingTrips: [], pendingPayload: null });
    await saveTrip(overlapAlert.pendingPayload);
  };

  const handleOverlapCancel = () => {
    setOverlapAlert({ open: false, overlappingTrips: [], pendingPayload: null });
  };

  const handleUkDatesConfirm = async () => {
    setLoading(true);
    setUkDatesWarning({ open: false, missingFields: [], pendingPayload: null });

    const overlappingTrips = runOverlapCheck();
    if (overlappingTrips.length > 0) {
      setOverlapAlert({ open: true, overlappingTrips, pendingPayload: ukDatesWarning.pendingPayload });
      setLoading(false);
      return;
    }

    await saveTrip(ukDatesWarning.pendingPayload);
  };

  const handleUkDatesCancel = () => {
    setUkDatesWarning({ open: false, missingFields: [], pendingPayload: null });
  };

  // Trip type helpers
  const isWorkTrip = formData.trip_type === 'work_trip';
  const isHotelStay = formData.trip_type === 'hotel_stay';
  const isUkHotelStay = formData.trip_type === 'uk_hotel_stay';
  const isCourseUk = formData.trip_type === 'course_uk';
  const isCourseForeign = formData.trip_type === 'course_foreign';
  const isCourse = isCourseUk || isCourseForeign;
  const isPaidLeave = formData.trip_type === 'paid_leave';
  const isWageAdjustment = formData.trip_type === 'wage_adjustment';
  const isBaseWorkUk = formData.trip_type === 'base_work_uk';
  const isBaseWorkForeign = formData.trip_type === 'base_work_foreign';
  const isBaseWork = isBaseWorkUk || isBaseWorkForeign;
  const isQualifyingHoliday = formData.trip_type === 'qualifying_holiday';
  const isVisaRun = formData.trip_type === 'visa_run';
  const isContractLinkable = isWorkTrip || isBaseWork || isHotelStay || isUkHotelStay || isCourse || isVisaRun || isPaidLeave;

  const stepFlags = { isWageAdjustment, isQualifyingHoliday, isContractLinkable, isWorkTrip };
  const validationFlags = { isContractLinkable, isWorkTrip, isHotelStay, isUkHotelStay, isCourse, isBaseWork, isVisaRun, isPaidLeave };
  const canGoNext = () => canGoNextStep(currentStep, formData, validationFlags);

  // Find the last trip's contract for "Same as last trip" feature
  const lastTripContract = React.useMemo(() => {
    if (!existingTrips.length || !contracts.length) return null;
    const sorted = [...existingTrips]
      .filter(t => t.contract_id)
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    if (!sorted.length) return null;
    return contracts.find(c => c.id === sorted[0].contract_id) || null;
  }, [existingTrips, contracts]);

  // Apply contract to form (auto-fill pay type and rates)
  const applyContract = (contract) => {
    if (!contract) return;
    const updates = { contract_id: contract.id };
    updates.pay_type = contract.contract_type === 'salaried' ? 'salaried' : 'day_rate';
    if (contract.contract_type === 'salaried') {
      updates.monthly_salary = contract.monthly_base || '';
      updates.sea_uplift_rate = contract.daily_uplift || '';
    }
    if (contract.travel_day_rate) {
      updates.has_separate_travel_rate = true;
      updates.travel_day_rate = contract.travel_day_rate;
    }
    if (contract.hotel_day_rate) {
      updates.has_separate_hotel_rate = true;
      updates.hotel_day_rate = contract.hotel_day_rate;
    }
    setFormData(prev => ({ ...prev, ...updates }));
    if (contract.contract_type === 'day_rate' && contract.daily_rate && dayRates.length > 0) {
      setDayRates(dayRates.map(r => ({ ...r, daily_rate: contract.daily_rate })));
    }
  };

  const goToNextStep = () => {
    if (currentStep < totalSteps && canGoNext()) setCurrentStep(currentStep + 1);
  };
  const goToPrevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  return (
    <DialogContent className="bg-slate-800 border-slate-700 w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="trip-dialog">
      <TripWizardHeader trip={trip} totalSteps={totalSteps} currentStep={currentStep} stepFlags={stepFlags} />

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-1">
        <div className="space-y-3 py-3">

          {/* === STEP 1: Contract & Trip Type === */}
          {currentStep === 1 && (
            <TripWizardStep1
              formData={formData} setFormData={setFormData}
              contracts={contracts} applyContract={applyContract} lastTripContract={lastTripContract}
              contractDialogOpen={contractDialogOpen} setContractDialogOpen={setContractDialogOpen}
              goToNextStep={goToNextStep}
              isContractLinkable={isContractLinkable} isWorkTrip={isWorkTrip}
              isPaidLeave={isPaidLeave} isWageAdjustment={isWageAdjustment}
              isUkHotelStay={isUkHotelStay} isCourseUk={isCourseUk} isHotelStay={isHotelStay}
              dateContext={dateContext}
            />
          )}

          {/* === STEP 2: Trip Details / Details & Dates === */}
          {currentStep === 2 && (
            <TripWizardStep2
              formData={formData} setFormData={setFormData}
              dayRates={dayRates} addDayRate={addDayRate} updateDayRate={dayRateOps.update} removeDayRate={dayRateOps.remove}
              uplifts={uplifts} addUplift={addUplift} updateUplift={upliftOps.update} removeUplift={upliftOps.remove}
              deductions={deductions} addDeduction={addDeduction} updateDeduction={deductionOps.update} removeDeduction={deductionOps.remove} calculateTotalDeductions={calculateTotalDeductions}
              dateContext={dateContext}
              isWorkTrip={isWorkTrip} isHotelStay={isHotelStay} isUkHotelStay={isUkHotelStay}
              isCourse={isCourse} isCourseForeign={isCourseForeign}
              isBaseWork={isBaseWork} isBaseWorkForeign={isBaseWorkForeign}
              isVisaRun={isVisaRun} isPaidLeave={isPaidLeave}
            />
          )}

          {/* === STEP 3: Dates & Travel (work_trip only) === */}
          {currentStep === 3 && isWorkTrip && (
            <>
              <WorkTripFields
                formData={formData}
                setFormData={setFormData}
                ukCrossings={ukCrossings}
                addUkCrossing={addUkCrossing}
                updateUkCrossing={ukCrossingOps.update}
                removeUkCrossing={ukCrossingOps.remove}
                showVesselFields={false}
                dateContext={dateContext}
              />

              <UpliftSection uplifts={uplifts} addUplift={addUplift} updateUplift={upliftOps.update} removeUplift={upliftOps.remove} dateContext={dateContext} />
              <DeductionsSection
                deductions={deductions}
                addDeduction={addDeduction}
                updateDeduction={deductionOps.update}
                removeDeduction={deductionOps.remove}
                calculateTotalDeductions={calculateTotalDeductions}
              />

              <div className="space-y-2">
                <Label className="text-slate-300">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                  rows={2}
                />
              </div>
            </>
          )}
        </div>
      </form>

      <DialogFooter className="border-t border-slate-700 pt-4 flex-shrink-0">
        <TripWizardFooter
          trip={trip} currentStep={currentStep} totalSteps={totalSteps} loading={loading}
          canGoNext={canGoNext()} onPrev={goToPrevStep} onNext={goToNextStep}
          onClose={onClose} onSubmit={handleSubmit} />
      </DialogFooter>

      <TripAlertDialogs
        ukDatesWarning={ukDatesWarning}
        handleUkDatesCancel={handleUkDatesCancel}
        handleUkDatesConfirm={handleUkDatesConfirm}
        overlapAlert={overlapAlert}
        handleOverlapCancel={handleOverlapCancel}
        handleOverlapConfirm={handleOverlapConfirm}
        formatDateDisplay={formatDateDisplay}
      />

      {/* Contract Dialog — opened from "Add Contract" button */}
      <ContractDialog
        open={contractDialogOpen}
        onOpenChange={setContractDialogOpen}
        contract={null}
        onSaved={handleContractCreated}
      />
    </DialogContent>
  );
};

export default TripDialog;
